import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  makeStyles,
  Grid,
  Typography,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import React from "react";
import { StatementsOfAssuranceRequest } from "../../../../../domain/AdditionalEvidenceRequest";
import { Controller, useForm, useWatch } from "react-hook-form";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { Error } from "../../../../common/Error";

type EvidenceDeclarationFormProps = {
  statement: StatementsOfAssuranceRequest | null;
  onClick: (entity: StatementsOfAssuranceRequest | "other" | null) => void;
  onSave: (data: StatementsOfAssuranceRequest) => void;
};

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: 15,
  },
  body: {
    marginTop: 5,
    marginBottom: 5,
  },
}));

export const EvidenceDeclaration: React.FC<EvidenceDeclarationFormProps> = ({
  statement,
  onClick,
  onSave,
}) => {
  const classes = useStyles();
  const {
    handleSubmit,
    control,
    errors,
    setValue,
    clearErrors,
  } = useForm<StatementsOfAssuranceRequest>({
    defaultValues: statement || {
      relatedDate: "",
      relatedDescription: "",
      declarationOptionID: "",
    },
    shouldUnregister: false,
  });

  const { declarationOptionID } = useWatch({
    control,
    name: ["declarationOptionID"],
  });

  return (
    <>
      {statement ? (
        <>
          <DialogTitle data-test="additional-evidence-title">
            {statement.declaration.name} - {statement.declaration.title}
            <Divider />
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <pre style={{ whiteSpace: "pre-line" }}>
                  <Typography variant="body2">
                    {statement.declaration.introductionText}
                  </Typography>
                </pre>
              </Grid>
              <Grid item xs={12}>
                <form>
                  {statement.declaration.options
                    .sort((a, b) => a.order - b.order)
                    .map((option, index) => {
                      return (
                        <Grid container spacing={3} key={option.id}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              name="declarationOptionID"
                              value={option.optionText}
                              control={<Radio />}
                              label={option.optionText}
                              checked={option.id === declarationOptionID}
                              data-test="declaration-option-id"
                              onChange={() => {
                                setValue("declarationOptionID", option.id);
                                setValue("relatedDate", null);
                                setValue("relatedDescription", null);
                                clearErrors("relatedDate");
                              }}
                            />
                          </Grid>
                          {option.hasRelatedDate &&
                          option.id === declarationOptionID ? (
                            <Grid item xs={12}>
                              <Controller
                                control={control}
                                name="relatedDate"
                                rules={{
                                  required: option.id === declarationOptionID,
                                }}
                                as={
                                  <DatePicker
                                    returnDayBoundary="endOfDay"
                                    label="Relevant date"
                                    required={option.id === declarationOptionID}
                                    disabled={option.id !== declarationOptionID}
                                    dataTest="relevant-date"
                                  />
                                }
                              />
                              {errors.relatedDate && (
                                <Error>This field is required</Error>
                              )}
                            </Grid>
                          ) : null}
                          {option.hasRelatedDescription &&
                          option.id === declarationOptionID ? (
                            <Grid item xs={12}>
                              <Controller
                                control={control}
                                name="relatedDescription"
                                rules={{
                                  required:
                                    option.id === declarationOptionID
                                      ? true
                                      : false,
                                }}
                                as={
                                  <TextField
                                    data-test="other-evidence-response-test"
                                    multiline
                                    rows={5}
                                    label="Relevant description"
                                    required={option.id === declarationOptionID}
                                    disabled={option.id !== declarationOptionID}
                                  />
                                }
                              />
                              {errors.relatedDescription && (
                                <Error>This field is required</Error>
                              )}
                            </Grid>
                          ) : null}
                        </Grid>
                      );
                    })}
                </form>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => onClick(null)}
              color="primary"
              data-test="back-additional-evidence-button"
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit(onSave)}
              color="primary"
              data-test="save-additional-evidence-button"
            >
              Save
            </Button>
          </DialogActions>
        </>
      ) : null}
    </>
  );
};
